import { useState, useEffect, useCallback } from "react";
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  ChevronDown,
  ChevronRight,
  Layers,
  Users2,
  Tag,
  DollarSign,
  GripVertical,
  Archive,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import {
  fetchCatalog,
  createCatalogItem,
  updateCatalogItem,
  archiveCatalogItem,
} from "../services/api";
import { useTenant } from "../contexts/TenantContext";

// ── Styles ────────────────────────────────────────────────────

const card = {
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: 20,
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  background: colors.bg,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

const selectStyle = { ...inputStyle, cursor: "pointer" };

const btnPrimary = {
  padding: "8px 18px",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fonts.body,
  background: colors.accent,
  color: "#ffffff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const btnSecondary = {
  ...btnPrimary,
  background: "transparent",
  color: colors.textSecondary,
  border: `1px solid ${colors.border}`,
};

const btnDanger = {
  ...btnPrimary,
  background: "rgba(239,68,68,0.15)",
  color: "#ef4444",
};

const CURRENCY_SYMBOLS = { gbp: "£", usd: "$", eur: "€" };
const CATEGORIES = [
  { value: "written_materials", label: "Written Materials" },
  { value: "data", label: "Data" },
  { value: "services", label: "Services" },
  { value: "other", label: "Other" },
];

function formatPrice(pence, currency = "gbp") {
  if (pence == null) return "—";
  const sym = CURRENCY_SYMBOLS[currency] || "£";
  return `${sym}${(pence / 100).toFixed(2)}`;
}

const TAB_CONFIG = [
  {
    key: "product",
    label: "Products",
    icon: Package,
    description: "Content and service products",
  },
  {
    key: "bundle",
    label: "Bundles",
    icon: Layers,
    description: "Product bundles with combined pricing",
  },
  {
    key: "client_type",
    label: "Access Tiers",
    icon: Users2,
    description: "Access tier templates with product access",
  },
];

// ── Main Component ────────────────────────────────────────────

function EmptyIcon({ type }) {
  const props = {
    size: 40,
    color: colors.textSecondary,
    style: { marginBottom: 12 },
  };
  if (type === "bundle") return <Layers {...props} />;
  if (type === "client_type") return <Users2 {...props} />;
  return <Package {...props} />;
}

export default function ProductCatalog() {
  const tenant = useTenant();
  const [activeTab, setActiveTab] = useState("product");
  const [items, setItems] = useState({
    product: [],
    bundle: [],
    client_type: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCatalog();
      const grouped = { product: [], bundle: [], client_type: [] };
      for (const item of data.items || []) {
        if (grouped[item.docType]) grouped[item.docType].push(item);
      }
      setItems(grouped);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      await createCatalogItem({ ...formData, docType: activeTab });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, formData) => {
    setSaving(true);
    try {
      await updateCatalogItem(id, { ...formData, docType: activeTab });
      setEditingItem(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (item) => {
    if (!confirm(`Archive "${item.name}"? It will be deactivated.`)) return;
    try {
      await archiveCatalogItem(item.id, item.docType);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const currentItems = items[activeTab] || [];
  const visibleItems = showArchived
    ? currentItems
    : currentItems.filter((i) => i.active !== false);
  const archivedCount = currentItems.filter((i) => i.active === false).length;
  const tabConfig = TAB_CONFIG.find((t) => t.key === activeTab);
  const allProducts = items.product || [];

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontFamily: fonts.heading,
              fontWeight: 700,
              margin: 0,
              color: colors.text,
            }}
          >
            Products
          </h1>
          <p
            style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}
          >
            Manage products, bundles, and access tier definitions
          </p>
        </div>
        <button
          style={btnPrimary}
          onClick={() => {
            setShowForm(true);
            setEditingItem(null);
          }}
        >
          <Plus size={15} /> New {tabConfig.label.replace(/s$/, "")}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            ...card,
            borderColor: colors.danger,
            marginBottom: 16,
            padding: 12,
            fontSize: 13,
            color: colors.danger,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: colors.danger,
              cursor: "pointer",
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 0,
          marginBottom: 20,
          borderBottom: `2px solid ${colors.border}`,
        }}
      >
        {TAB_CONFIG.map((tab) => {
          const Icon = tab.icon;
          const count = (items[tab.key] || []).filter(
            (i) => i.active !== false,
          ).length;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setShowForm(false);
                setEditingItem(null);
              }}
              style={{
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: fonts.body,
                background: "none",
                border: "none",
                borderBottom: isActive
                  ? `2px solid ${colors.accent}`
                  : "2px solid transparent",
                color: isActive ? colors.accent : colors.textSecondary,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: -2,
              }}
            >
              <Icon size={15} /> {tab.label}
              <span
                style={{
                  background: isActive ? colors.accentDim : colors.bgHover,
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Form */}
      {(showForm || editingItem) && (
        <CatalogForm
          docType={activeTab}
          item={editingItem}
          allProducts={allProducts}
          saving={saving}
          currency={tenant.currency || "gbp"}
          onSubmit={
            editingItem ? (d) => handleUpdate(editingItem.id, d) : handleCreate
          }
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {/* List */}
      {loading ? (
        <div
          style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}
        >
          <Loader2 size={24} className="spin" />
          <p style={{ marginTop: 12, fontSize: 13 }}>Loading catalog...</p>        </div>
      ) : visibleItems.length === 0 && !showForm ? (
        <div style={{ ...card, textAlign: "center", padding: 60 }}>
          <EmptyIcon type={activeTab} />
          <p
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            No {tabConfig.label.toLowerCase()} yet
          </p>
          <p
            style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 16 }}
          >
            {tabConfig.description}
          </p>
          <button style={btnPrimary} onClick={() => setShowForm(true)}>
            <Plus size={15} /> Create {tabConfig.label.replace(/s$/, "")}
          </button>
        </div>
      ) : (
        <>
          {activeTab === "product" && (
            <ProductList
              items={visibleItems}
              onEdit={setEditingItem}
              onArchive={handleArchive}
            />
          )}
          {activeTab === "bundle" && (
            <BundleList
              items={visibleItems}
              allProducts={allProducts}
              onEdit={setEditingItem}
              onArchive={handleArchive}
            />
          )}
          {activeTab === "client_type" && (
            <ClientTypeList
              items={visibleItems}
              allProducts={allProducts}
              onEdit={setEditingItem}
              onArchive={handleArchive}
            />
          )}

          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              style={{ ...btnSecondary, marginTop: 16, fontSize: 12 }}
            >
              <Archive size={13} /> {showArchived ? "Hide" : "Show"}{" "}
              {archivedCount} archived
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Product List ──────────────────────────────────────────────

function ProductList({ items, onEdit, onArchive }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/* Header row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 100px",
          gap: 12,
          padding: "8px 16px",
          fontSize: 11,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        <span>Product</span>
        <span>Code</span>
        <span>Category</span>
        <span>Rack Rate</span>
        <span>Internal Cost</span>
        <span></span>
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            ...card,
            padding: "12px 16px",
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 100px",
            gap: 12,
            alignItems: "center",
            opacity: item.active === false ? 0.5 : 1,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
              {item.name}
            </div>
            {item.description && (
              <div
                style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}
              >
                {item.description}
              </div>
            )}
          </div>
          <span
            style={{
              fontSize: 12,
              fontFamily: "monospace",
              color: colors.textSecondary,
              background: colors.bgHover,
              padding: "2px 8px",
              borderRadius: 4,
              display: "inline-block",
              width: "fit-content",
            }}
          >
            {item.code}
          </span>
          <span style={{ fontSize: 12, color: colors.textSecondary }}>
            {CATEGORIES.find((c) => c.value === item.category)?.label ||
              item.category}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
            {formatPrice(item.rackRate, item.currency)}
          </span>
          <span style={{ fontSize: 13, color: colors.textSecondary }}>
            {formatPrice(item.internalCost, item.currency)}
          </span>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              onClick={() => onEdit(item)}
              style={{
                background: "none",
                border: "none",
                color: colors.textSecondary,
                cursor: "pointer",
                padding: 4,
              }}
              title="Edit"
            >
              <Pencil size={14} />
            </button>
            {item.active !== false && (
              <button
                onClick={() => onArchive(item)}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.danger,
                  cursor: "pointer",
                  padding: 4,
                }}
                title="Archive"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Bundle List ───────────────────────────────────────────────

function BundleList({ items, allProducts, onEdit, onArchive }) {
  const productMap = Object.fromEntries(allProducts.map((p) => [p.id, p]));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 2fr 1fr 1fr 100px",
          gap: 12,
          padding: "8px 16px",
          fontSize: 11,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        <span>Bundle</span>
        <span>Included Products</span>
        <span>Rack Rate</span>
        <span>Internal Cost</span>
        <span></span>
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            ...card,
            padding: "12px 16px",
            display: "grid",
            gridTemplateColumns: "2fr 2fr 1fr 1fr 100px",
            gap: 12,
            alignItems: "center",
            opacity: item.active === false ? 0.5 : 1,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
              {item.name}
            </div>
            {item.description && (
              <div
                style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}
              >
                {item.description}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {(item.productIds || []).map((pid) => (
              <span
                key={pid}
                style={{
                  fontSize: 11,
                  background: colors.accentDim,
                  color: colors.accent,
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                {productMap[pid]?.name || pid}
              </span>
            ))}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
            {formatPrice(item.rackRate, item.currency)}
          </span>
          <span style={{ fontSize: 13, color: colors.textSecondary }}>
            {formatPrice(item.internalCost, item.currency)}
          </span>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              onClick={() => onEdit(item)}
              style={{
                background: "none",
                border: "none",
                color: colors.textSecondary,
                cursor: "pointer",
                padding: 4,
              }}
            >
              <Pencil size={14} />
            </button>
            {item.active !== false && (
              <button
                onClick={() => onArchive(item)}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.danger,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Client Type List ──────────────────────────────────────────

function ClientTypeList({ items, allProducts, onEdit, onArchive }) {
  const productMap = Object.fromEntries(allProducts.map((p) => [p.id, p]));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 3fr 100px",
          gap: 12,
          padding: "8px 16px",
          fontSize: 11,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        <span>Access Tier</span>
        <span>Included Products</span>
        <span></span>
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            ...card,
            padding: "12px 16px",
            display: "grid",
            gridTemplateColumns: "2fr 3fr 100px",
            gap: 12,
            alignItems: "center",
            opacity: item.active === false ? 0.5 : 1,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
              {item.name}
            </div>
            {item.description && (
              <div
                style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}
              >
                {item.description}
              </div>
            )}
            <span
              style={{
                fontSize: 11,
                fontFamily: "monospace",
                color: colors.textSecondary,
                background: colors.bgHover,
                padding: "1px 6px",
                borderRadius: 3,
                marginTop: 4,
                display: "inline-block",
              }}
            >
              {item.code}
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {(item.includedProductIds || []).length === 0 ? (
              <span
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  fontStyle: "italic",
                }}
              >
                No products assigned
              </span>
            ) : (
              (item.includedProductIds || []).map((pid) => (
                <span
                  key={pid}
                  style={{
                    fontSize: 11,
                    background: colors.accentDim,
                    color: colors.accent,
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontWeight: 500,
                  }}
                >
                  {productMap[pid]?.name || pid}
                </span>
              ))
            )}
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              onClick={() => onEdit(item)}
              style={{
                background: "none",
                border: "none",
                color: colors.textSecondary,
                cursor: "pointer",
                padding: 4,
              }}
            >
              <Pencil size={14} />
            </button>
            {item.active !== false && (
              <button
                onClick={() => onArchive(item)}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.danger,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Unified Form ──────────────────────────────────────────────

function CatalogForm({
  docType,
  item,
  allProducts,
  saving,
  currency,
  onSubmit,
  onCancel,
}) {
  const isEdit = !!item;
  const [name, setName] = useState(item?.name || "");
  const [code, setCode] = useState(item?.code || "");
  const [description, setDescription] = useState(item?.description || "");
  const [category, setCategory] = useState(item?.category || "other");
  const [rackRate, setRackRate] = useState(
    item?.rackRate != null ? (item.rackRate / 100).toFixed(2) : "",
  );
  const [internalCost, setInternalCost] = useState(
    item?.internalCost != null ? (item.internalCost / 100).toFixed(2) : "",
  );
  const [displayOrder, setDisplayOrder] = useState(item?.displayOrder || 99);
  const [selectedProductIds, setSelectedProductIds] = useState(
    item?.productIds || item?.includedProductIds || [],
  );

  const sym = CURRENCY_SYMBOLS[currency] || "£";
  const isProduct = docType === "product";
  const isBundle = docType === "bundle";
  const isClientType = docType === "client_type";
  const typeLabel = isProduct ? "Product" : isBundle ? "Bundle" : "Access Tier";

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      name: name.trim(),
      code:
        code.trim() ||
        name
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "_"),
      description: description.trim(),
      displayOrder: Number(displayOrder) || 99,
    };

    if (isProduct) {
      data.category = category;
      data.rackRate = rackRate ? Math.round(parseFloat(rackRate) * 100) : null;
      data.internalCost = internalCost
        ? Math.round(parseFloat(internalCost) * 100)
        : null;
      data.currency = currency;
    }

    if (isBundle) {
      data.productIds = selectedProductIds;
      data.rackRate = rackRate ? Math.round(parseFloat(rackRate) * 100) : null;
      data.internalCost = internalCost
        ? Math.round(parseFloat(internalCost) * 100)
        : null;
      data.currency = currency;
    }

    if (isClientType) {
      data.includedProductIds = selectedProductIds;
    }

    onSubmit(data);
  };

  const toggleProduct = (pid) => {
    setSelectedProductIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid],
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...card, marginBottom: 20 }}>
      <h3
        style={{
          fontSize: 15,
          fontWeight: 700,
          fontFamily: fonts.heading,
          margin: "0 0 16px",
          color: colors.text,
        }}
      >
        {isEdit ? `Edit ${typeLabel}` : `New ${typeLabel}`}
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          Name *
          <input
            style={inputStyle}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`${typeLabel} name`}
            required
          />
        </label>
        <label
          style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}
        >
          Code
          <input
            style={inputStyle}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="AUTO_GENERATED"
          />
        </label>
      </div>

      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          display: "block",
          marginBottom: 12,
        }}
      >
        Description
        <input
          style={inputStyle}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description"
        />
      </label>

      {isProduct && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Category
            <select
              style={selectStyle}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Rack Rate ({sym})
            <input
              style={inputStyle}
              type="number"
              step="0.01"
              min="0"
              value={rackRate}
              onChange={(e) => setRackRate(e.target.value)}
              placeholder="0.00"
            />
          </label>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Internal Cost ({sym})
            <input
              style={inputStyle}
              type="number"
              step="0.01"
              min="0"
              value={internalCost}
              onChange={(e) => setInternalCost(e.target.value)}
              placeholder="0.00"
            />
          </label>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Display Order
            <input
              style={inputStyle}
              type="number"
              min="1"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
            />
          </label>
        </div>
      )}

      {isBundle && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Rack Rate ({sym})
            <input
              style={inputStyle}
              type="number"
              step="0.01"
              min="0"
              value={rackRate}
              onChange={(e) => setRackRate(e.target.value)}
              placeholder="0.00"
            />
          </label>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Internal Cost ({sym})
            <input
              style={inputStyle}
              type="number"
              step="0.01"
              min="0"
              value={internalCost}
              onChange={(e) => setInternalCost(e.target.value)}
              placeholder="0.00"
            />
          </label>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Display Order
            <input
              style={inputStyle}
              type="number"
              min="1"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
            />
          </label>
        </div>
      )}

      {isClientType && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            Display Order
            <input
              style={{ ...inputStyle, width: 100 }}
              type="number"
              min="1"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
            />
          </label>
        </div>
      )}

      {/* Product Picker (for bundles and client types) */}
      {(isBundle || isClientType) && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 8,
            }}
          >
            Included Products
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {allProducts
              .filter((p) => p.active !== false)
              .map((p) => {
                const selected = selectedProductIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleProduct(p.id)}
                    style={{
                      padding: "6px 14px",
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: fonts.body,
                      background: selected ? colors.accent : colors.bgHover,
                      color: selected ? "#fff" : colors.textSecondary,
                      border: `1px solid ${selected ? colors.accent : colors.border}`,
                      borderRadius: 20,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {selected && <Check size={12} style={{ marginRight: 4 }} />}
                    {p.name}
                  </button>
                );
              })}
            {allProducts.filter((p) => p.active !== false).length === 0 && (
              <span
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  fontStyle: "italic",
                }}
              >
                Create products first before adding them to{" "}
                {isBundle ? "bundles" : "access tiers"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" onClick={onCancel} style={btnSecondary}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          style={{ ...btnPrimary, opacity: saving || !name.trim() ? 0.6 : 1 }}
        >
          {saving ? (
            <Loader2
              size={14}
              className="spin"
            />
          ) : (
            <Check size={14} />
          )}
          {isEdit ? "Update" : "Create"} {typeLabel}
        </button>
      </div>
    </form>
  );
}
